import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStudent } from "@/context/StudentContext";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { LayoutDashboard, FileSignature, Upload, BookOpen, ClipboardCheck, Bell, LogOut } from "lucide-react";
import { StudentOverview } from "./sections/StudentOverview";
import { StudentDocuments } from "./sections/StudentDocuments";
import { StudentUploads } from "./sections/StudentUploads";
import { StudentCourses } from "./sections/StudentCourses";
import { StudentQuizzes } from "./sections/StudentQuizzes";
import { StudentNotifications } from "./sections/StudentNotifications";
import { SetPasswordPrompt } from "./sections/SetPasswordPrompt";

const TABS = [
  { key: "overview", label: "Dashboard", icon: LayoutDashboard },
  { key: "documents", label: "Documents", icon: FileSignature },
  { key: "uploads", label: "Uploads", icon: Upload },
  { key: "courses", label: "Course Materials", icon: BookOpen },
  { key: "quizzes", label: "Quizzes", icon: ClipboardCheck },
  { key: "notifications", label: "Notifications", icon: Bell },
];

export default function StudentPortal() {
  const { student, loading, logout } = useStudent();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/student/dashboard").then((r) => setData(r.data)).catch((e) => {
      if ([401, 403].includes(e?.response?.status)) navigate("/student/login", { replace: true });
      else toast.error("Could not load your portal");
    });
  }, [navigate]);

  useEffect(() => {
    if (loading) return;
    if (!student) { navigate("/student/login", { replace: true }); return; }
    load();
  }, [loading, student, load, navigate]);

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-navy-light"><p className="animate-pulse font-display font-bold text-navy">Loading your Student Portal…</p></div>;

  const s = data.student;
  const counts = {
    documents: data.documents.filter((d) => !d.completed_at).length,
    uploads: data.requests.filter((r) => !["Accepted", "Pending Review"].includes(r.status)).length,
    courses: data.materials.filter((m) => !m.completed_at).length,
    quizzes: data.quizzes.filter((q) => q.attempts_used === 0).length,
    notifications: data.unread,
  };

  return (
    <div className="min-h-screen bg-soft-gray" data-testid="student-portal">
      <header className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-md px-2 py-1"><Logo className="h-8" /></span>
            <span className="hidden sm:block font-display font-bold text-sm">Driven Together <span className="text-magenta">Student Portal</span></span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:block text-sm text-white/80" data-testid="student-header-name">{s.name}</span>
            <button onClick={async () => { await logout(); navigate("/student/login"); }} data-testid="student-logout"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-sm font-semibold py-2 px-3 rounded-md transition-colors">
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto" data-testid="student-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`student-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-4 transition-colors ${tab === t.key ? "border-magenta text-white" : "border-transparent text-white/60 hover:text-white"}`}>
              <t.icon size={16} /> {t.label}
              {counts[t.key] > 0 && <span className="bg-magenta text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px]">{counts[t.key]}</span>}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {params.get("setpw") && <SetPasswordPrompt email={s.email} />}
        {tab === "overview" && <StudentOverview data={data} reload={load} goTo={setTab} welcome={!!params.get("welcome")} />}
        {tab === "documents" && <StudentDocuments docs={data.documents} reload={load} student={s} />}
        {tab === "uploads" && <StudentUploads requests={data.requests} reload={load} />}
        {tab === "courses" && <StudentCourses materials={data.materials} reload={load} />}
        {tab === "quizzes" && <StudentQuizzes quizzes={data.quizzes} reload={load} />}
        {tab === "notifications" && <StudentNotifications items={data.notifications} reload={load} />}
      </main>
    </div>
  );
}
